import React, { useMemo } from 'react'
import { Button, Popconfirm, Spin, Tag, Typography } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { useIntl } from 'react-intl'

import { ConversationBadge } from '@vacationtracker/shared/types/conversation'
import IntlMessages from '../../util/IntlMessages'
import { VeeMark } from './vee-mark'
import { IVeeConversationInfo } from './types'

dayjs.extend(localizedFormat)

interface IVeeChatsProps {
  conversations: IVeeConversationInfo[]
  loading: boolean
  hasMore?: boolean
  loadingMore?: boolean
  onShowMore?: () => void
  deleteDisabledId?: string
  onOpenConversation: (id: string) => void
  onNewChat: () => void
  onDeleteConversation: (id: string) => void
}

const BADGE_KEYS: Record<ConversationBadge, string> = {
  request_opened: 'veeAssistant.badge.requestOpened',
  updated: 'veeAssistant.badge.updated',
  cancelled: 'veeAssistant.badge.cancelled',
}

function formatStamp(updatedAt: string): string {
  const date = dayjs(updatedAt)
  const now = dayjs()
  if (now.diff(date, 'minute') < 60) {
    return `${Math.max(now.diff(date, 'minute'), 1)}m`
  }
  if (date.isSame(now, 'day')) {
    return date.format('HH:mm')
  }
  if (date.isSame(now, 'week')) {
    return date.format('ddd')
  }
  return date.format('D MMM')
}

function groupLabel(updatedAt: string, formatMessage: (descriptor: { id: string }) => string): string {
  const date = dayjs(updatedAt)
  const now = dayjs()
  if (date.isSame(now, 'day')) {
    return formatMessage({ id: 'app.today' })
  }
  if (date.isSame(now.subtract(1, 'day'), 'day')) {
    return formatMessage({ id: 'veeAssistant.groupYesterday' })
  }
  if (date.isSame(now, 'week')) {
    return formatMessage({ id: 'veeAssistant.groupEarlierThisWeek' })
  }
  return date.format('MMMM YYYY')
}

export const VeeChats: React.FC<IVeeChatsProps> = ({
  conversations,
  loading,
  hasMore,
  loadingMore,
  onShowMore,
  deleteDisabledId,
  onOpenConversation,
  onNewChat,
  onDeleteConversation,
}) => {
  const { formatMessage } = useIntl()

  // The resolver already returns newest-first (LSI1 desc on updatedAt); only the grouping is ours.
  const groups = useMemo(() => {
    const result: Array<{ label: string; items: IVeeConversationInfo[] }> = []
    for (const conversation of conversations) {
      const label = groupLabel(conversation.updatedAt || conversation.timestamp || '', formatMessage)
      const group = result.find((g) => g.label === label)
      if (group) {
        group.items.push(conversation)
      } else {
        result.push({ label, items: [conversation] })
      }
    }
    return result
  }, [conversations, formatMessage])

  if (loading) {
    return (
      <div className="vee-chats vee-chats-loading">
        <Spin />
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <div className="vee-chats vee-chats-empty">
        <VeeMark size={48} />
        <Typography.Text strong>
          <IntlMessages id="veeAssistant.historyEmptyTitle" />
        </Typography.Text>
        <Typography.Text type="secondary">
          <IntlMessages id="veeAssistant.historyEmptySubtitle" />
        </Typography.Text>
        <Button type="primary" onClick={onNewChat}>
          <IntlMessages id="veeAssistant.historyEmptyAction" />
        </Button>
      </div>
    )
  }

  return (
    <div className="vee-chats">
      {groups.map((group) => (
        <div key={group.label} className="vee-chats-group">
          <Typography.Text type="secondary" className="vee-chats-group-label">
            {group.label}
          </Typography.Text>
          {group.items.map((conversation) => {
            const stamp = conversation.updatedAt || conversation.timestamp || ''
            return (
              <div key={conversation.id} className="vee-chats-row-wrap">
                <button type="button" className="vee-chats-row" onClick={() => onOpenConversation(conversation.id)}>
                  <span className="vee-chats-row-main">
                    <span className="vee-chats-row-topic">{conversation.topic || formatMessage({ id: 'veeAssistant.newChat' })}</span>
                    <span className="vee-chats-row-stamp" title={dayjs(stamp).format('LLLL')}>
                      {formatStamp(stamp)}
                    </span>
                  </span>
                  {conversation.badge && BADGE_KEYS[conversation.badge] && (
                    <Tag className="vee-chats-row-badge">
                      <IntlMessages id={BADGE_KEYS[conversation.badge]} />
                    </Tag>
                  )}
                </button>
                <Popconfirm
                  title={<IntlMessages id="veeAssistant.deleteConversationConfirm" />}
                  okText={formatMessage({ id: 'app.delete' })}
                  cancelText={formatMessage({ id: 'app.cancel' })}
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDeleteConversation(conversation.id)}
                >
                  <Button
                    className="vee-chats-row-delete"
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    disabled={conversation.id === deleteDisabledId}
                    aria-label={formatMessage({ id: 'app.delete' })}
                  />
                </Popconfirm>
              </div>
            )
          })}
        </div>
      ))}
      {hasMore && (
        <Button className="vee-chats-show-more" loading={loadingMore} onClick={onShowMore}>
          <IntlMessages id="app.showMore" />
        </Button>
      )}
    </div>
  )
}
